import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar } from '@mui/material';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';

const UserEdit = () => {
    useSessionValidation();

    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
        }
    }, [isAuthenticated, companyId]);

    const [formData, setFormData] = useState({
        FirstName: '', LastName: '', UserName: '',
        Email: '', ContactNumber: ''
    });

    useEffect(() => {
        fetchData('UserApi', `/UserDetails/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => console.error('Error fetching User Details:', error));
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const updated = { ...prev, [name]: value };
            if (name === 'FirstName' || name === 'LastName') {
                const fn = name === 'FirstName' ? value : prev.FirstName;
                const ln = name === 'LastName' ? value : prev.LastName;
                updated.UserName = `${fn.toLowerCase()}_${ln.toLowerCase()}`.slice(0, 15);
            }
            return updated;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateData('UserApi', `${id}`, { ...formData, UpdatedBy: userId }, token)
            .then(() => {
                setSuccessMessage('User updated successfully!');
                setTimeout(() => history.push('/UserIndex'), 1000);
            })
            .catch(() => setErrorMessage('Failed to update user.'));
    };

    const inputStyle = {
        width: '100%', padding: '6px 10px', fontSize: '13px',
        borderRadius: '8px', border: '1px solid #e2e8f0',
        background: 'white', outline: 'none', fontFamily: 'inherit',
        boxSizing: 'border-box'
    };

    const labelStyle = {
        display: 'block', fontSize: '11px', fontWeight: '600',
        color: '#4a5568', marginBottom: '4px',
        textTransform: 'uppercase', letterSpacing: '0.3px'
    };

    const sectionStyle = {
        border: '1px solid #e2e8f0', borderRadius: '10px',
        padding: '12px', marginBottom: '10px', background: '#fafafa'
    };

    const sectionTitleStyle = {
        display: 'flex', alignItems: 'center', gap: '6px',
        fontSize: '12px', fontWeight: '700', color: '#2C3E6E',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '10px', paddingBottom: '6px',
        borderBottom: '1px solid #e2e8f0'
    };

    return (
        <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Roman' }}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, fontFamily: 'Times New Roman', textTransform: 'uppercase', color: 'black' }}>
                        Edit User
                    </h4>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            startIcon={<SaveIcon sx={{ fontSize: '16px' }} />}
                            sx={{
                                background: '#2C3E6E', color: 'white', padding: '4px 12px',
                                borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap'
                            }}
                        >
                            Update
                        </Button>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/UserIndex"
                            startIcon={<ArrowBackIcon sx={{ fontSize: '16px' }} />}
                            sx={{
                                borderColor: '#2C3E6E', color: '#2C3E6E', padding: '4px 12px',
                                borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap'
                            }}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                {/* Personal Information */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>
                        <PersonIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        Personal Information
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                        <div>
                            <label style={labelStyle}>First Name *</label>
                            <input name="FirstName" value={formData.FirstName} onChange={handleChange} type="text" placeholder="Enter first name" maxLength={30} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Last Name *</label>
                            <input name="LastName" value={formData.LastName} onChange={handleChange} type="text" placeholder="Enter last name" maxLength={30} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>User Name</label>
                            <input name="UserName" value={formData.UserName} type="text" readOnly style={{ ...inputStyle, background: '#f5f5f5', cursor: 'not-allowed' }} />
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>
                        <PhoneIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        Contact Details
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                        <div>
                            <label style={labelStyle}>Contact Number *</label>
                            <input name="ContactNumber" value={formData.ContactNumber} onChange={handleChange} type="text" placeholder="Enter contact number" maxLength={15} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Email Address *</label>
                            <input name="Email" value={formData.Email} onChange={handleChange} type="email" placeholder="user@example.com" maxLength={40} style={inputStyle} />
                        </div>
                    </div>
                </div>
            </div>

            <Snackbar open={!!errorMessage || !!successMessage} autoHideDuration={4000} onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} severity={errorMessage ? 'error' : 'success'}>
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserEdit;
